// ---------------------------------------------------------------------------
// Cookie helpers
// ---------------------------------------------------------------------------

/**
 * Set a cookie with the given key, value, and max-age in seconds.
 * Uses SameSite=Lax and Secure when on HTTPS.
 */
export function setCookie(
  key: string,
  value: string,
  maxAgeSec: number,
): void {
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)}; max-age=${maxAgeSec}; path=/${secure}; SameSite=Lax`;
}

/**
 * Read a cookie value by key. Returns undefined if not found.
 */
export function getCookie(key: string): string | undefined {
  const encoded = encodeURIComponent(key);
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${encoded}=`));
  if (!match) return undefined;
  return decodeURIComponent(match.split("=")[1]);
}

// ---------------------------------------------------------------------------
// Cooltime (suppress re-display)
// ---------------------------------------------------------------------------

/**
 * Build the cookie key used for cooltime suppression.
 */
export function cooltimeCookieKey(groupId: string): string {
  return `popup_config_group_${groupId}_popup_suppression`;
}

/**
 * Set cooltime cookie to suppress popup re-display.
 */
export function setCooltimeCookie(
  groupId: string,
  durationSec: number,
): void {
  setCookie(cooltimeCookieKey(groupId), "1", durationSec);
}

/**
 * Check if the popup is currently in cooltime (should not display).
 */
export function isInCooltime(groupId: string): boolean {
  return getCookie(cooltimeCookieKey(groupId)) !== undefined;
}

// ---------------------------------------------------------------------------
// URL Parameter → Cookie persistence (CV tracking)
// ---------------------------------------------------------------------------

/**
 * Mapping from URL query parameters to cookie names.
 * When the SDK loads on any page, these params are saved as cookies
 * so that downstream CV pages can read them later.
 */
const URL_PARAM_COOKIE_MAP: Record<string, string> = {
  luid: "lr_luid",
  cbcv_from: "lr_cbcv_from",
  workspace_id: "lr_workspace_id",
  scenario_id: "lr_scenario_id",
  node_id: "lr_node_id",
  line_official_account_id: "lr_line_official_account_id",
  popup_config_id: "lr_popup_config_id",
};

/** 60 days in seconds */
const TRACKING_COOKIE_MAX_AGE = 60 * 24 * 60 * 60;

/**
 * Read URL query parameters and persist them as first-party cookies.
 * Only writes a cookie when the corresponding query parameter is present.
 */
export function saveUrlParamsToCookies(): void {
  const params = new URLSearchParams(window.location.search);
  for (const [param, cookieName] of Object.entries(URL_PARAM_COOKIE_MAP)) {
    const value = params.get(param);
    if (value) {
      setCookie(cookieName, value, TRACKING_COOKIE_MAX_AGE);
    }
  }
}

/**
 * All cookie names managed by the tracking system.
 */
const ALL_TRACKING_COOKIE_NAMES = [
  "lr_session_id",
  "lr_luid",
  "lr_cbcv_from",
  "lr_workspace_id",
  "lr_scenario_id",
  "lr_node_id",
  "lr_line_official_account_id",
  "lr_popup_config_id",
] as const;

/**
 * Read all tracking cookies and return them as a key-value object.
 * Only cookies that are currently set are included.
 */
export function getAllTrackingCookies(): Record<string, string> {
  const result: Record<string, string> = {};
  for (const name of ALL_TRACKING_COOKIE_NAMES) {
    const value = getCookie(name);
    if (value !== undefined) {
      result[name] = value;
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Device detection
// ---------------------------------------------------------------------------

const MOBILE_UA_RE =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

/**
 * Detect if the current device is mobile based on User-Agent.
 */
export function isMobileDevice(): boolean {
  return MOBILE_UA_RE.test(navigator.userAgent);
}

/**
 * Detect if running on iOS 16+ Safari.
 * iOS 16+ Safari changes history.pushState behavior, requiring deferred injection.
 */
export function isIos16PlusSafari(): boolean {
  const ua = navigator.userAgent;
  // Must be Safari on iOS (not Chrome/Firefox on iOS)
  if (!/Safari/i.test(ua) || /CriOS|FxiOS|OPiOS/i.test(ua)) {
    return false;
  }
  const match = ua.match(/OS (\d+)_/);
  if (!match) return false;
  return parseInt(match[1], 10) >= 16;
}

// ---------------------------------------------------------------------------
// UUID v4 generation
// ---------------------------------------------------------------------------

/**
 * Generate a UUID v4 string using crypto.getRandomValues when available,
 * falling back to Math.random.
 */
export function generateUUID(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ---------------------------------------------------------------------------
// Session / PageView ID management (sessionStorage-based)
// ---------------------------------------------------------------------------

const SESSION_ID_KEY = "lr_session_id";
const PAGE_VIEW_ID_KEY = "lr_page_view_id";

/**
 * Get or create a persistent session ID (survives page navigations within
 * the same tab, cleared when tab closes).
 */
export function getSessionId(): string {
  let id = sessionStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = generateUUID();
    sessionStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
}

/**
 * Generate a fresh page-view ID for each page load.
 */
export function getPageViewId(): string {
  const id = generateUUID();
  sessionStorage.setItem(PAGE_VIEW_ID_KEY, id);
  return id;
}

// ---------------------------------------------------------------------------
// Time-period validation
// ---------------------------------------------------------------------------

/**
 * Check if the current time (JST) is within the display window.
 * Mirrors chatboost logic: offset by +9h UTC then compare ISO strings.
 */
export function isWithinDisplayPeriod(
  lowerBound: string,
  upperBound: string,
): boolean {
  const now = new Date();
  // chatboost adds 9 hours (324e5 ms = 9 * 3600 * 1000) to get JST
  const jstIso = new Date(now.getTime() + 9 * 3600 * 1000).toISOString();
  return jstIso >= lowerBound && jstIso <= upperBound;
}

// ---------------------------------------------------------------------------
// Referrer check
// ---------------------------------------------------------------------------

/**
 * Check if the referrer is from the same domain (for intra-domain suppression).
 */
export function isReferrerSameDomain(): boolean {
  if (!document.referrer) return false;
  const pattern = new RegExp(
    `^https?://${escapeRegExp(window.location.hostname)}`,
    "i",
  );
  return pattern.test(document.referrer);
}

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// ---------------------------------------------------------------------------
// Throttle utility
// ---------------------------------------------------------------------------

/**
 * Simple trailing-edge throttle.
 */
export function throttle<T extends (...args: unknown[]) => void>(
  ms: number,
  fn: T,
): T {
  let lastCall = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  return ((...args: unknown[]) => {
    const now = Date.now();
    const remaining = ms - (now - lastCall);
    if (remaining <= 0) {
      lastCall = now;
      fn(...args);
    } else if (!timer) {
      timer = setTimeout(() => {
        lastCall = Date.now();
        timer = null;
        fn(...args);
      }, remaining);
    }
  }) as T;
}
